import { ChevronDown, ChevronUp } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

import Input from './input';

interface IProps {
  value: number;
  onChange: (value: number) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  min?: number;
  max?: number;
  step?: number;
  style?: React.CSSProperties;
  label?: string;
  precision?: number; // Количество знаков после запятой (точность округления)
}

function NumberInput(props: IProps) {
  const { value, onChange, onFocus, onBlur, min, max, step = 1, style, label, precision = 0 } = props;
  const [inputValue, setInputValue] = useState(value.toFixed(precision));

  useEffect(() => {
    setInputValue(Number(value).toFixed(precision));
  }, [value, precision]);

  const handleInputChange = useCallback((newValue: string) => {
    setInputValue(newValue);
  }, []);

  // Функция для округления числа с заданной точностью
  const roundNumber = useCallback(
    (num: number): number => {
      const factor = Math.pow(10, precision);
      return Math.round(num * factor) / factor;
    },
    [precision],
  );

  const handleBlur = useCallback(() => {
    const numValue = parseFloat(inputValue);
    if (!isNaN(numValue)) {
      let newValue = roundNumber(numValue);

      // Применяем ограничения
      if (min !== undefined && newValue < min) {
        newValue = min;
      }
      if (max !== undefined && newValue > max) {
        newValue = max;
      }

      onChange(newValue);
      setInputValue(newValue.toFixed(precision));
    } else {
      // Если введено некорректное число, возвращаем предыдущее значение
      setInputValue(Number(value).toFixed(precision));
    }

    if (onBlur) {
      onBlur();
    }
  }, [inputValue, onChange, value, min, max, onBlur, roundNumber, precision]);

  const increment = useCallback(() => {
    const numValue = parseFloat(inputValue);
    if (!isNaN(numValue)) {
      let newValue = roundNumber(numValue + step);

      // Применяем ограничение максимального значения
      if (max !== undefined && newValue > max) {
        newValue = max;
      }

      setInputValue(newValue.toFixed(precision));
      onChange(newValue);
    }
  }, [inputValue, onChange, step, max, roundNumber, precision]);

  const decrement = useCallback(() => {
    const numValue = parseFloat(inputValue);
    if (!isNaN(numValue)) {
      let newValue = roundNumber(numValue - step);

      // Применяем ограничение минимального значения
      if (min !== undefined && newValue < min) {
        newValue = min;
      }

      setInputValue(newValue.toFixed(precision));
      onChange(newValue);
    }
  }, [inputValue, onChange, step, min, roundNumber, precision]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      // Перехватываем событие нажатия клавиш
      if (event.key === 'Enter') {
        handleBlur();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault(); // Предотвращаем прокрутку страницы
        increment();
      } else if (event.key === 'ArrowDown') {
        event.preventDefault(); // Предотвращаем прокрутку страницы
        decrement();
      }
    },
    [handleBlur, increment, decrement],
  );

  return (
    <div
      className="number-input-container"
      style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%', ...style }}
    >
      <Input
        value={inputValue}
        onChange={handleInputChange}
        onFocus={onFocus}
        onBlur={handleBlur}
        style={{ width: '100%' }}
        onKeyDown={handleKeyDown}
      />
      <div
        className="arrows-container"
        style={{
          position: 'absolute',
          right: '8px',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          justifyContent: 'center',
        }}
      >
        <div
          className="arrow-button"
          onClick={increment}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <ChevronUp size={14} className="cursor-pointer" onClick={increment} />
        </div>
        <div
          className="arrow-button"
          onClick={decrement}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <ChevronDown size={14} className="cursor-pointer" onClick={decrement} />
        </div>
      </div>
    </div>
  );
}

export default NumberInput;
